// trendlineEngine.js
// Dow Theory trend + trailing SL engine — PURE computation, no chart code.
//
// CHANGE (pivot SL): trend STATE + when-to-ratchet still come from the box
// (close-based breakouts). But the SL LEVEL now rides the most-recent
// confirmed n-bar PIVOT (swing low in up / swing high in down), instead of
// the box's absolute extreme. This fixes the SL sitting at a deep wick-low
// (up) or a stale spike-high (down).
//
// Unit of work: evaluateCandle(prevState, candle, pivots).
//   pivots = { low: {time,value}|null, high: {time,value}|null }
//            = most-recent CONFIRMED pivot as of this candle.
//   computeTrendline(candles, n) supplies the pivots and folds the loop.
//
// candle : { time, open, high, low, close }   (time = unix seconds)
// n      : pivot lookback — a pivot needs n lower/higher candles each side.
//          A pivot at candle X is confirmed only at candle X+n (inherent lag).
//
// TUNE POINTS still open: [TUNE 1] warm-up seed, [TUNE 2] pending resolution.
// ============================================================

export const ACTION = {
  WAIT: 'WAIT', HOLD_LONG: 'HOLD_LONG', HOLD_SHORT: 'HOLD_SHORT',
  ENTER_LONG: 'ENTER_LONG', ENTER_SHORT: 'ENTER_SHORT',
  EXIT_LONG: 'EXIT_LONG', EXIT_SHORT: 'EXIT_SHORT',
};

export const STATE = {
  NEUTRAL: 'neutral', UP: 'up', DOWN: 'down',
  PENDING_DOWN: 'pending_down', PENDING_UP: 'pending_up',
};

// ============================================================
// PIVOT HELPERS — candle at index pi is a pivot if its low/high is the
// extreme across the window [pi-n .. pi+n]. Caller guarantees window exists.
// ============================================================
function isPivotLow(candles, pi, n) {
  const v = candles[pi].low;
  for (let j = pi - n; j <= pi + n; j++) {
    if (j === pi) continue;
    if (candles[j].low < v) return false;   // someone lower -> not a pivot low
  }
  return true;
}
function isPivotHigh(candles, pi, n) {
  const v = candles[pi].high;
  for (let j = pi - n; j <= pi + n; j++) {
    if (j === pi) continue;
    if (candles[j].high > v) return false;
  }
  return true;
}

// ============================================================
// [TUNE 1] initState — seed neutral range box from the first candle.
// ============================================================
export function initState(candle) {
  return {
    state:        STATE.NEUTRAL,
    range_high:   candle.high,
    range_low:    candle.low,
    committed_SL: null,
    sl_time:      null,     // time of the candle whose low/high the SL sits on
    prior_high:   null,
    prior_low:    null,
    action:       ACTION.WAIT,
    sl_level:     null,
    time:         candle.time,
  };
}

// ── resolve the SL level+time from pivots, fall back to box extreme ──
function slUp(s, pivots)   { return pivots.low  ? pivots.low  : { time: null, value: s.range_low  }; }
function slDown(s, pivots) { return pivots.high ? pivots.high : { time: null, value: s.range_high }; }

// ============================================================
// evaluateCandle — prevState + one closed candle + pivots -> new state.
// ============================================================
export function evaluateCandle(prev, candle, pivots = { low: null, high: null }) {
  const s = { ...prev, time: candle.time, action: null };
  switch (prev.state) {
    case STATE.UP:           return evalUp(s, candle, pivots);
    case STATE.DOWN:         return evalDown(s, candle, pivots);
    case STATE.PENDING_DOWN: return evalPendingDown(s, candle, pivots);
    case STATE.PENDING_UP:   return evalPendingUp(s, candle, pivots);
    case STATE.NEUTRAL:
    default:                 return evalNeutral(s, candle, pivots);
  }
}

function extendBox(s, c) {
  s.range_high = Math.max(s.range_high, c.high);
  s.range_low  = Math.min(s.range_low,  c.low);
}
function resetBox(s, c) { s.range_high = c.high; s.range_low = c.low; }

// ── enter UP: SL = RANGE FLOOR at entry (recent consolidation), NOT a pivot.
//    At a fresh flip the most-recent pivot low is the OLD downtrend's bottom
//    (far below price) — using it anchors the SL too deep. Pivots drive only
//    the mid-trend ratchet (evalUp), whose "move up only" guard then ignores
//    that stale low automatically. ──
function enterUp(s, c, action) {
  const slVal    = s.range_low;
  s.prior_high   = s.range_high;
  s.prior_low    = slVal;
  s.state        = STATE.UP;
  s.committed_SL = slVal;
  s.sl_time      = null;
  s.sl_level     = slVal;
  s.action       = action;
  resetBox(s, c);
  return s;
}
function enterDown(s, c, action) {
  const slVal    = s.range_high;   // ceiling of the consolidation being broken
  s.prior_low    = s.range_low;
  s.prior_high   = slVal;
  s.state        = STATE.DOWN;
  s.committed_SL = slVal;
  s.sl_time      = null;
  s.sl_level     = slVal;
  s.action       = action;
  resetBox(s, c);
  return s;
}

// ============================================================
// NEUTRAL — extend box until first CLOSE breaks it.
// ============================================================
function evalNeutral(s, c, pivots) {
  if (c.close > s.range_high) return enterUp(s, c, ACTION.ENTER_LONG);
  if (c.close < s.range_low)  return enterDown(s, c, ACTION.ENTER_SHORT);
  extendBox(s, c);
  s.action = ACTION.WAIT; s.sl_level = null;
  return s;
}

// ============================================================
// UP — hold long. Exit on close < SL. Ratchet on close > range_high,
// moving SL up to the most-recent pivot low (never down).
// ============================================================
function evalUp(s, c, pivots) {
  if (c.close < s.committed_SL) return toPendingDown(s, c);        // exit
  if (c.close > s.range_high) {                                    // ratchet up
    const t = slUp(s, pivots);
    if (t.value > s.committed_SL) { s.committed_SL = t.value; s.sl_time = t.time; }
    s.prior_high = s.range_high;
    s.sl_level   = s.committed_SL;
    s.action     = ACTION.HOLD_LONG;
    resetBox(s, c);
    return s;
  }
  extendBox(s, c);                                                 // extend
  s.sl_level = s.committed_SL;
  s.action   = ACTION.HOLD_LONG;
  return s;
}

// ============================================================
// DOWN — mirror. Exit on close > SL. Ratchet on close < range_low,
// moving SL down to the most-recent pivot high (never up).
// ============================================================
function evalDown(s, c, pivots) {
  if (c.close > s.committed_SL) return toPendingUp(s, c);
  if (c.close < s.range_low) {
    const t = slDown(s, pivots);
    if (t.value < s.committed_SL) { s.committed_SL = t.value; s.sl_time = t.time; }
    s.prior_low = s.range_low;
    s.sl_level  = s.committed_SL;
    s.action    = ACTION.HOLD_SHORT;
    resetBox(s, c);
    return s;
  }
  extendBox(s, c);
  s.sl_level = s.committed_SL;
  s.action   = ACTION.HOLD_SHORT;
  return s;
}

function toPendingDown(s, c) {
  s.state = STATE.PENDING_DOWN; s.prior_low = s.committed_SL;
  s.sl_level = s.committed_SL; s.action = ACTION.EXIT_LONG;
  resetBox(s, c);
  return s;
}
function toPendingUp(s, c) {
  s.state = STATE.PENDING_UP; s.prior_high = s.committed_SL;
  s.sl_level = s.committed_SL; s.action = ACTION.EXIT_SHORT;
  resetBox(s, c);
  return s;
}

// ============================================================
// [TUNE 2] PENDING states — whipsaw vs confirm.
// ============================================================
function evalPendingDown(s, c, pivots) {
  if (c.close > s.prior_high) return enterUp(s, c, ACTION.ENTER_LONG);    // whipsaw
  if (c.close < s.range_low)  return enterDown(s, c, ACTION.ENTER_SHORT); // confirm
  extendBox(s, c);
  s.sl_level = s.committed_SL; s.action = ACTION.WAIT;
  return s;
}
function evalPendingUp(s, c, pivots) {
  if (c.close < s.prior_low)  return enterDown(s, c, ACTION.ENTER_SHORT); // whipsaw
  if (c.close > s.range_high) return enterUp(s, c, ACTION.ENTER_LONG);    // confirm
  extendBox(s, c);
  s.sl_level = s.committed_SL; s.action = ACTION.WAIT;
  return s;
}

// ============================================================
// computeTrendline — batch. Confirms pivots as it walks and feeds them in.
//   Pivot at index (i-n) is confirmed when we reach index i.
// Returns { states, linePoints }.
// ============================================================
export function computeTrendline(candles, n = 3) {
  const states = [];
  const linePoints = [];
  if (!candles || candles.length === 0) return { states, linePoints };

  let lastPivotLow  = null;   // {time, value}
  let lastPivotHigh = null;

  let state = initState(candles[0]);
  states.push(state);
  if (state.sl_level != null) linePoints.push({ time: state.time, value: state.sl_level });

  for (let i = 1; i < candles.length; i++) {
    // confirm pivot at index (i - n), window [i-2n .. i]
    const pi = i - n;
    if (pi - n >= 0) {
      if (isPivotLow(candles, pi, n))  lastPivotLow  = { time: candles[pi].time, value: candles[pi].low };
      if (isPivotHigh(candles, pi, n)) lastPivotHigh = { time: candles[pi].time, value: candles[pi].high };
    }
    const pivots = { low: lastPivotLow, high: lastPivotHigh };

    state = evaluateCandle(state, candles[i], pivots);
    states.push(state);
    if (state.sl_level != null) linePoints.push({ time: state.time, value: state.sl_level });
  }
  return { states, linePoints };
}